import _ from 'lodash'

// Hours at Location (ICE-27): the displayed value is days (one business day = 24h), shown to one
// decimal place (e.g. 72.2h -> "3.0"). The live value is derived from the UTC anchors below.
export const formatHoursAtLocationDays = (hours) =>
  hours == null ? '' : (Number(hours) / 24).toFixed(1)

// Parse a backend UTC datetime ("YYYY-MM-DD HH:MM:SS", no zone) into a Date. Returns null for
// empty/invalid input. These anchors are always emitted in UTC, so we make the zone explicit before
// parsing — a bare space-separated string would otherwise be read as LOCAL time. If the string
// already carries a zone designator (a trailing Z or ±HH:MM, e.g. an ISO timestamp), we leave it
// alone rather than appending a second "Z" (which would make the date unparseable).
export const parseUtcDateTime = (value) => {
  if (!value) return null
  let d
  if (typeof value === 'string') {
    let raw = value.replace(' ', 'T')
    if (!/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) raw += 'Z'
    d = new Date(raw)
  } else {
    d = new Date(value)
  }
  return isNaN(d.getTime()) ? null : d
}

// Business (Mon-Fri) hours between two instants, computed in UTC so weekend boundaries line up with
// the PHP/Python implementations. Args are Date objects or ms timestamps. Returns decimal hours; 0
// when end <= start. Mirror of Contract::businessHoursBetween (ICE-27).
export const businessHoursBetween = (start, end) => {
  const s = start instanceof Date ? start.getTime() : start
  const e = end instanceof Date ? end.getTime() : end
  if (!s || !e || e <= s) return 0
  let seconds = 0
  let cursor = s
  while (cursor < e) {
    const d = new Date(cursor)
    const nextMidnight = Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate() + 1
    )
    const segmentEnd = Math.min(nextMidnight, e)
    const dow = d.getUTCDay() // 0 = Sun ... 6 = Sat
    if (dow >= 1 && dow <= 5) {
      seconds += (segmentEnd - cursor) / 1000
    }
    cursor = nextMidnight
  }
  return seconds / 3600
}

export const setContractColumns = (params, properties) => {
  return params.map((param) => {
    if (isNaN(param)) {
      switch (param) {
        case 'id':
          return {
            field: param,
            label: '#',
            sort: 'desc',
          }
        case 'current_state_text':
          return {
            field: param,
            label: 'State',
            sort: 'desc',
          }
        case 'study.name':
          return {
            field: param,
            label: 'Sponsor Study Name',
            sort: 'desc',
          }
        case 'contract_type.name':
          return {
            field: param,
            label: 'Type',
            sort: 'desc',
          }
        case 'contract_subtype.name':
          return {
            field: param,
            label: 'Subtype',
            sort: 'desc',
          }
        case 'current_activity.contract_status':
          return {
            field: param,
            label: 'Current Status',
            sort: 'desc',
          }
        case 'current_activity.contract_location':
          return {
            field: param,
            label: 'Current Location',
            sort: 'desc',
          }
        case 'current_activity.contract_sublocation':
          return {
            field: param,
            label: 'Current Sublocation',
            sort: 'desc',
          }
        case 'current_activity.activity_occurred_at':
          return {
            date: true,
            field: param,
            label: 'Last Active (UTC)',
            sort: 'desc',
          }
        case 'buttons':
          return {
            field: param,
            label: '',
            minimal: 'lg',
            sort: 'disabled',
          }
        default:
          return false
      }
    } else {
      const propertyResult = _.find(
        properties.custom_properties,
        (property) => {
          if (property.property.id === Number(param)) {
            return true
          }
        }
      )
      return propertyResult
        ? {
            field: propertyResult.property.id.toString(),
            isCustom: true,
            label: propertyResult.property.name,
            path: `custom_properties['${propertyResult.property.name}']`,
          }
        : false
    }
  })
}

export const setSrDocumentColumns = (params, properties) => {
  return params.map((param) => {
    if (isNaN(param)) {
      switch (param) {
        case 'id':
          return {
            field: param,
            label: '#',
            sort: 'desc',
          }
        case 'current_state_text':
          return {
            field: param,
            label: 'State',
            sort: 'desc',
          }
        case 'study.name':
          return {
            field: param,
            label: 'Sponsor Study Name',
            sort: 'desc',
          }
        case 'sr_document_type.name':
          return {
            field: param,
            label: 'Type',
            sort: 'desc',
          }
        case 'sr_document_subtype.name':
          return {
            field: param,
            label: 'Subtype',
            sort: 'desc',
          }
        case 'current_activity.sr_document_status':
          return {
            field: param,
            label: 'Current Status',
            sort: 'desc',
          }
        case 'current_activity.sr_document_location':
          return {
            field: param,
            label: 'Current Location',
            sort: 'desc',
          }
        case 'current_activity.activity_occurred_at':
          return {
            date: true,
            field: param,
            label: 'Last Active (UTC)',
            sort: 'desc',
          }
        case 'buttons':
          return {
            field: param,
            label: '',
            minimal: 'lg',
            sort: 'disabled',
          }
        default:
          return false
      }
    } else {
      const propertyResult = _.find(
        properties.custom_properties,
        (property) => {
          if (property.property.id === Number(param)) {
            return true
          }
        }
      )
      return propertyResult
        ? {
            field: propertyResult.property.id.toString(),
            isCustom: true,
            label: propertyResult.property.name,
            path: `custom_properties['${propertyResult.property.name}']`,
          }
        : false
    }
  })
}

export const filterTableColumns = (tableData, setTableData, columnNames) => {
  if (tableData.columns.find((c) => columnNames.includes(c.field))) {
    tableData.columns = tableData.columns.filter((c) => {
      return !columnNames.includes(c.field)
    })
    setTableData(tableData)
  }
}

export const defaultContractColumns = [
  {
    label: '#',
    field: 'id',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'State',
    field: 'current_state_text',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Sponsor Study Name',
    field: 'study.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Country',
    field: 'primary_site_country.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Site',
    field: 'primary_site.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Investigator',
    field: 'primary_investigator.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Type',
    field: 'contract_type.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Subtype',
    field: 'contract_subtype.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Current Status',
    field: 'current_activity.contract_status',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'Current Location',
    field: 'current_activity.contract_location',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'Current Sublocation',
    field: 'current_activity.contract_sublocation',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'Last Active (UTC)',
    field: 'current_activity.activity_occurred_at',
    date: true,
    sort: 'desc',
    minimal: 'lg',
    defaultActive: true,
  },
]

export const defaultSrDocumentColumns = [
  {
    label: '#',
    field: 'id',
    sort: 'desc',
    defaultActive: true,
  },
  /*
  this is being hidden at clients request.
  {
    label: 'State',
    field: 'current_state_text',
    sort: 'asc',
    defaultActive: true,
  },
  */
  {
    label: 'Sponsor Study Name',
    field: 'study.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Country',
    field: 'primary_site_country.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Site',
    field: 'primary_site.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Investigator',
    field: 'primary_investigator.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Type',
    field: 'sr_document_type.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Subtype',
    field: 'sr_document_subtype.name',
    sort: 'asc',
    defaultActive: true,
  },
  {
    label: 'Current Status',
    field: 'current_activity.sr_document_status',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'Current Location',
    field: 'current_activity.sr_document_location',
    sort: 'desc',
    defaultActive: true,
  },
  {
    label: 'Last Active (UTC)',
    field: 'current_activity.activity_occurred_at',
    date: true,
    sort: 'desc',
    minimal: 'lg',
    defaultActive: true,
  },
]
